import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Spinner, Alert, Card, Row, Col } from 'react-bootstrap';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import CredentialVaultInitInfoPanel from './CredentialVaultInitInfoPanel';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  deriveKeys,
  unwrapDekFromPayload,
  prepareCorporateVaultSetup,
  base64ToBytes,
  zeroBytes,
  isCorporateAdminRole,
  isCorporateVaultAccount,
} from '../../crypto/credentialVaultCrypto';
import {
  getVaultCryptoStatus,
  getVaultSalt,
  unlockVaultApi,
  setupVaultApi,
  getPersonalVaultCryptoStatus,
  unlockPersonalVaultDekFromPersonalPassword,
} from '../../services/CredentialVaultService';
import {
  isCredentialVaultVaultSettingsPath,
  isCredentialVaultFormEntryPath,
  isCredentialVaultDetailPath,
  normalizeAccessPath,
} from '../../utils/routeAccessConfig';
import {
  setCredentialVaultNavAddState,
  clearCredentialVaultNavAddState,
} from '../../utils/credentialVaultNavAddBridge';
import '../settings/commonstyle.css';
import '../users/userlist.css';
import './credentialVault.css';
import '../tasks/tasklist.css';
import '../tasks/addtask.css';

const CredentialVaultCryptoContext = createContext(null);

/** Set `REACT_APP_CREDENTIAL_VAULT_DEBUG=true` in `.env` and restart dev server — logs idle timers, DEK clears, route/sticky side (filter DevTools console by `[CredentialVault]`). */
function credentialVaultDebug(...args) {
  const on =
    process.env.REACT_APP_CREDENTIAL_VAULT_DEBUG === 'true' ||
    process.env.REACT_APP_CREDENTIAL_VAULT_DEBUG === '1';
  if (!on) return;
  console.log('[CredentialVault]', new Date().toISOString(), ...args);
}

/** Pass as `navigate(url, { state: credentialVaultSideState('personal') })` / `'corporate'` for detail & edit routes. */
export function credentialVaultSideState(side) {
  return { credentialVaultSide: side };
}

/**
 * Which password domain the user is in: corporate (master) vs personal (personal vault password).
 * Used to clear both sessions when switching so one unlock cannot cover the other area.
 */
export function deriveCredentialVaultSide(pathname, search, routeState) {
  if (!pathname || typeof pathname !== 'string') return null;
  const p =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

  if (p.endsWith('/personalVaultList')) return 'personal';
  if (p.endsWith('/credentialVaultList')) return 'corporate';

  /** Neutral for sticky/session purge — avoids wiping personal DEK when opening settings from the personal list. */
  if (p === '/credential-vault/vaultSettings' || p.startsWith('/credential-vault/vaultSettings/')) {
    return null;
  }

  if (p.includes('/addCredential')) {
    const sideFromState = routeState?.credentialVaultSide;
    if (sideFromState === 'personal' || sideFromState === 'corporate') {
      return sideFromState;
    }
    const raw = search && typeof search === 'string' ? search.replace(/^\?/, '') : '';
    const q = new URLSearchParams(raw);
    return q.get('scope') === 'personal' ? 'personal' : 'corporate';
  }

  const side = routeState?.credentialVaultSide;
  if (side === 'personal' || side === 'corporate') return side;

  return null;
}

/** Pass when navigating from add/edit Back or after save so the list does not re-prompt for vault passwords on landing. */
export function credentialVaultListReturnState(side) {
  return { credentialVaultSide: side, credentialVaultSkipListPasswordOnce: true };
}

/** Pass when leaving a credential list so detail/edit/add Back can restore ?search=. */
export function credentialVaultListNavigateState(side, listSearch) {
  return {
    credentialVaultSide: side,
    credentialVaultListSearch: listSearch != null ? String(listSearch) : '',
  };
}

/** pathname + ?search= for list routes from optional location.state */
export function buildCredentialVaultListLocation(pathname, routeState) {
  const q = routeState?.credentialVaultListSearch;
  const trimmed = q != null ? String(q).trim() : '';
  const search =
    trimmed !== '' ? `?${new URLSearchParams({ search: trimmed }).toString()}` : '';
  return { pathname, search };
}

export function CredentialVaultGateShell({ children }) {
  return (
    <div className="credential-vault-gate-page">
      <div className="credential-vault-gate-page__center">{children}</div>
      <InternalFooter />
    </div>
  );
}

/** Minutes until the Credentials Vault locks again after no activity (master password required). Override via REACT_APP_CREDENTIAL_VAULT_IDLE_MINUTES */
function getVaultIdleTimeoutMinutes() {
  const raw = process.env.REACT_APP_CREDENTIAL_VAULT_IDLE_MINUTES;
  const parsed = raw != null && raw !== '' ? parseInt(raw, 10) : 15;
  const mins = Number.isFinite(parsed) && parsed > 0 ? parsed : 15;
  return mins;
}

export function useCredentialVaultCrypto() {
  return useContext(CredentialVaultCryptoContext);
}

export function CredentialVaultCryptoProvider({ children }) {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [vaultInitialized, setVaultInitialized] = useState(false);
  const [dek, setDek] = useState(null);
  const [setupPw, setSetupPw] = useState('');
  const [setupPw2, setSetupPw2] = useState('');
  const [unlockPw, setUnlockPw] = useState('');
  const [corporateGateUnlockPrimed, setCorporateGateUnlockPrimed] = useState(false);
  const prevDekRef = useRef(null);
  const [showUnlockPw, setShowUnlockPw] = useState(false);
  const [showSetupPw, setShowSetupPw] = useState(false);
  const [showSetupPw2, setShowSetupPw2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [personalVaultInitialized, setPersonalVaultInitialized] = useState(false);
  const [personalDek, setPersonalDek] = useState(null);

  const idleTimerRef = useRef(null);
  const lastBumpRef = useRef(0);
  const personalIdleTimerRef = useRef(null);
  const personalLastBumpRef = useRef(0);
  const stickyVaultSideRef = useRef(null);

  const idleMinutes = useMemo(() => getVaultIdleTimeoutMinutes(), []);
  const idleMs = idleMinutes * 60 * 1000;

  useEffect(() => {
    credentialVaultDebug('provider mounted');
    return () => {
      credentialVaultDebug(
        'provider UNMOUNTED (navigated away from /credential-vault/*) — in-memory DEK is discarded'
      );
    };
  }, []);

  useEffect(() => {
    credentialVaultDebug('idle config', {
      REACT_APP_CREDENTIAL_VAULT_IDLE_MINUTES: process.env.REACT_APP_CREDENTIAL_VAULT_IDLE_MINUTES,
      resolvedIdleMinutes: idleMinutes,
      resolvedIdleMs: idleMs,
      NODE_ENV: process.env.NODE_ENV,
    });
  }, [idleMinutes, idleMs]);

  useEffect(() => {
    credentialVaultDebug('corporate dek presence', { hasDek: !!dek });
  }, [dek]);

  useEffect(() => {
    credentialVaultDebug('personal dek presence', { hasPersonalDek: !!personalDek });
  }, [personalDek]);

  // Publish unlock status for the global navbar Add button (Header is outside this provider).
  useEffect(() => {
    const side = deriveCredentialVaultSide(
      location.pathname,
      location.search,
      location.state
    );
    setCredentialVaultNavAddState({
      active: true,
      side: side === 'personal' || side === 'corporate' ? side : null,
      corporateUnlocked: !!dek,
      personalUnlocked: !!personalDek,
    });
  }, [dek, personalDek, location.pathname, location.search, location.state]);

  useEffect(() => {
    return () => {
      clearCredentialVaultNavAddState();
    };
  }, []);

  useEffect(() => {
    if (prevDekRef.current != null && dek == null) {
      setCorporateGateUnlockPrimed(false);
    }
    prevDekRef.current = dek;
  }, [dek]);

  const clearPersonalIdleTimer = useCallback(() => {
    if (personalIdleTimerRef.current != null) {
      window.clearTimeout(personalIdleTimerRef.current);
      personalIdleTimerRef.current = null;
    }
  }, []);

  const schedulePersonalIdleLock = useCallback(() => {
    clearPersonalIdleTimer();
    const firesAt = Date.now() + idleMs;
    credentialVaultDebug('schedule personal idle lock', {
      idleMinutes,
      idleMs,
      firesAtIso: new Date(firesAt).toISOString(),
    });
    personalIdleTimerRef.current = window.setTimeout(() => {
      personalIdleTimerRef.current = null;
      credentialVaultDebug('PERSONAL IDLE TIMER FIRED — clearing personal DEK', { idleMinutes });
      setPersonalDek((prev) => {
        if (prev) {
          void zeroBytes(prev);
        }
        return null;
      });
      toast.info(
        `Personal vault locked after ${idleMinutes} minute${idleMinutes === 1 ? '' : 's'} of inactivity.`,
        { position: 'top-right', autoClose: 6000 }
      );
    }, idleMs);
  }, [clearPersonalIdleTimer, idleMinutes, idleMs]);

  const bumpPersonalVaultActivity = useCallback(() => {
    const now = Date.now();
    if (now - personalLastBumpRef.current < 750) return;
    personalLastBumpRef.current = now;
    schedulePersonalIdleLock();
  }, [schedulePersonalIdleLock]);

  const isAdmin = useMemo(() => isCorporateAdminRole(), []);
  const isCorporateAccount = useMemo(() => isCorporateVaultAccount(), []);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current != null) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const scheduleIdleLock = useCallback(() => {
    clearIdleTimer();
    const firesAt = Date.now() + idleMs;
    credentialVaultDebug('schedule corporate idle lock', {
      idleMinutes,
      idleMs,
      firesAtIso: new Date(firesAt).toISOString(),
    });
    idleTimerRef.current = window.setTimeout(() => {
      idleTimerRef.current = null;
      credentialVaultDebug('CORPORATE IDLE TIMER FIRED — clearing corporate DEK', { idleMinutes });
      setDek((prev) => {
        if (prev) {
          void zeroBytes(prev);
        }
        return null;
      });
      toast.info(
        `Credentials Vault locked after ${idleMinutes} minute${idleMinutes === 1 ? '' : 's'} of inactivity. Enter your master password again.`,
        { position: 'top-right', autoClose: 6000 }
      );
    }, idleMs);
  }, [clearIdleTimer, idleMinutes, idleMs]);

  const bumpVaultActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastBumpRef.current < 750) return;
    lastBumpRef.current = now;
    scheduleIdleLock();
  }, [scheduleIdleLock]);

  /** Keeps vault unlocked while viewing credential detail (no-op if corporate DEK not loaded). */
  const touchCredentialVaultIdleFromActivity = useCallback(() => {
    if (!dek) return;
    bumpVaultActivity();
  }, [dek, bumpVaultActivity]);

  /** Keeps personal vault unlocked while viewing credential detail (no-op if personal DEK not loaded). */
  const touchPersonalVaultIdleFromActivity = useCallback(() => {
    if (!personalDek) return;
    bumpPersonalVaultActivity();
  }, [personalDek, bumpPersonalVaultActivity]);

  const clearBothVaultSessions = useCallback(() => {
    credentialVaultDebug('clearBothVaultSessions invoked');
    clearIdleTimer();
    clearPersonalIdleTimer();
    setDek((prev) => {
      if (prev) void zeroBytes(prev);
      return null;
    });
    setPersonalDek((prev) => {
      if (prev) void zeroBytes(prev);
      return null;
    });
  }, [clearIdleTimer, clearPersonalIdleTimer]);

  /** Switching corporate ↔ personal clears both DEKs so master and personal passwords must be entered again for that area. */
  useEffect(() => {
    const derived = deriveCredentialVaultSide(
      location.pathname,
      location.search,
      location.state
    );
    const prevSticky = stickyVaultSideRef.current;
    credentialVaultDebug('sticky route derived', {
      pathname: location.pathname,
      search: location.search,
      locationKey: location.key,
      derived,
      prevSticky,
      stateSide: location.state?.credentialVaultSide,
    });
    if (derived != null) {
      if (prevSticky != null && derived !== prevSticky) {
        credentialVaultDebug('sticky side changed — clearing BOTH vault sessions', {
          prevSticky,
          derived,
        });
        clearBothVaultSessions();
      }
      stickyVaultSideRef.current = derived;
    }
  }, [location.pathname, location.search, location.state, location.key, clearBothVaultSessions]);

  const refreshStatus = useCallback(async () => {
    const res = await getVaultCryptoStatus();
    const ok = res.data?.success && res.data?.data?.vaultInitialized === true;
    setVaultInitialized(!!ok);
    return !!ok;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        await refreshStatus();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshStatus]);

  useEffect(() => {
    if (!vaultInitialized || dek) {
      setUnlockPw('');
      setShowUnlockPw(false);
    }
  }, [vaultInitialized, dek]);

  useEffect(() => {
    if (!vaultInitialized || !dek) {
      clearIdleTimer();
      return undefined;
    }
    bumpVaultActivity();
    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click', 'wheel'];
    events.forEach((ev) => window.addEventListener(ev, bumpVaultActivity, { passive: true }));
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, bumpVaultActivity));
      clearIdleTimer();
    };
  }, [vaultInitialized, dek, bumpVaultActivity, clearIdleTimer]);

  /** Always reschedule idle lock on navigation (bypasses bumpVaultActivity's 750ms throttle — rapid list ↔ detail must keep the vault unlocked). */
  useEffect(() => {
    if (!vaultInitialized || !dek) return;
    credentialVaultDebug('reschedule corporate idle (route change)', {
      pathname: location.pathname,
      key: location.key,
    });
    scheduleIdleLock();
  }, [
    location.pathname,
    location.search,
    location.key,
    vaultInitialized,
    dek,
    scheduleIdleLock,
  ]);

  const vaultReady = vaultInitialized && !!dek;

  const credentialVaultSide = deriveCredentialVaultSide(
    location.pathname,
    location.search,
    location.state
  );
  const isPersonalVaultRoute = credentialVaultSide === 'personal';

  /** Personal vault routes work without corporate vault initialization or master unlock. */
  const personalVaultRouteBypass = isPersonalVaultRoute;

  const vaultSettingsRouteBypass = isCredentialVaultVaultSettingsPath(
    normalizeAccessPath(location.pathname)
  );

  const normalizedCredentialPath = normalizeAccessPath(location.pathname);

  /** Skip list password gates once when returning from add/edit (Back or save) — see `credentialVaultListReturnState`. */
  const credentialVaultListPasswordReturnSkip =
    location.state?.credentialVaultSkipListPasswordOnce === true &&
    ((normalizedCredentialPath === '/credential-vault/credentialVaultList' &&
      location.state?.credentialVaultSide === 'corporate') ||
      (normalizedCredentialPath === '/credential-vault/personalVaultList' &&
        location.state?.credentialVaultSide === 'personal'));

  /** Corporate list: allow outlet without master unlock for that landing. */
  const corporateListPasswordSkipBypass =
    credentialVaultListPasswordReturnSkip &&
    vaultInitialized &&
    isCorporateAccount &&
    normalizedCredentialPath === '/credential-vault/credentialVaultList';

  /** Personal list: suppress unlock panel for that landing (`CredentialVaultListView`). */
  const skipPersonalListPasswordPromptOnce =
    credentialVaultListPasswordReturnSkip &&
    normalizedCredentialPath === '/credential-vault/personalVaultList';

  /** Corporate add/edit forms: show UI without master unlock; password is collected when saving. */
  const credentialVaultFormEntryBypass =
    vaultInitialized &&
    isCorporateAccount &&
    isCredentialVaultFormEntryPath(normalizedCredentialPath) &&
    !isPersonalVaultRoute &&
    !vaultSettingsRouteBypass;

  /** Credential detail: view metadata without corporate unlock (master password only on list / save / delete). */
  const credentialVaultDetailBypass =
    vaultInitialized &&
    isCorporateAccount &&
    isCredentialVaultDetailPath(normalizedCredentialPath) &&
    !isPersonalVaultRoute &&
    !vaultSettingsRouteBypass;

  const showVaultOutlet =
    vaultReady ||
    personalVaultRouteBypass ||
    vaultSettingsRouteBypass ||
    credentialVaultFormEntryBypass ||
    credentialVaultDetailBypass ||
    corporateListPasswordSkipBypass;

  const refreshPersonalVaultStatus = useCallback(async () => {
    try {
      const res = await getPersonalVaultCryptoStatus();
      const ok = res.data?.success && res.data?.data?.vaultInitialized === true;
      setPersonalVaultInitialized(!!ok);
      return !!ok;
    } catch {
      setPersonalVaultInitialized(false);
      return false;
    }
  }, []);

  useEffect(() => {
    if (
      !vaultReady &&
      !personalVaultRouteBypass &&
      !vaultSettingsRouteBypass &&
      !credentialVaultFormEntryBypass &&
      !credentialVaultDetailBypass &&
      !corporateListPasswordSkipBypass
    )
      return;
    void refreshPersonalVaultStatus();
  }, [
    vaultReady,
    personalVaultRouteBypass,
    vaultSettingsRouteBypass,
    credentialVaultFormEntryBypass,
    credentialVaultDetailBypass,
    corporateListPasswordSkipBypass,
    refreshPersonalVaultStatus,
  ]);

  useEffect(() => {
    if (!personalDek) {
      clearPersonalIdleTimer();
      return undefined;
    }
    bumpPersonalVaultActivity();
    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click', 'wheel'];
    events.forEach((ev) => window.addEventListener(ev, bumpPersonalVaultActivity, { passive: true }));
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, bumpPersonalVaultActivity));
      clearPersonalIdleTimer();
    };
  }, [personalDek, bumpPersonalVaultActivity, clearPersonalIdleTimer]);

  useEffect(() => {
    if (!personalDek) return;
    credentialVaultDebug('reschedule personal idle (route change)', {
      pathname: location.pathname,
      key: location.key,
    });
    schedulePersonalIdleLock();
  }, [
    location.pathname,
    location.search,
    location.key,
    personalDek,
    schedulePersonalIdleLock,
  ]);

  const unlockPersonalVaultSession = useCallback(
    async (password) => {
      const dekBytes = await unlockPersonalVaultDekFromPersonalPassword(password);
      setPersonalDek((prev) => {
        if (prev) {
          void zeroBytes(prev);
        }
        return dekBytes;
      });
      return dekBytes;
    },
    []
  );

  /** Call after POST personal/setup — retains unlocked DEK in session without extra password prompt. */
  const retainPersonalVaultDekAfterSetup = useCallback((dekBytes) => {
    setPersonalDek((prev) => {
      if (prev) {
        void zeroBytes(prev);
      }
      return dekBytes;
    });
    void refreshPersonalVaultStatus();
  }, [refreshPersonalVaultStatus]);

  /** Clears in-memory personal DEK (e.g. after changing personal vault password in settings). */
  const clearPersonalVaultSession = useCallback(() => {
    credentialVaultDebug('clearPersonalVaultSession');
    clearPersonalIdleTimer();
    setPersonalDek((prev) => {
      if (prev) void zeroBytes(prev);
      return null;
    });
  }, [clearPersonalIdleTimer]);

  /** Keep corporate DEK in session after unlock-from-password flows that bypass handleUnlock (e.g. save modal). */
  const retainCorporateVaultDekAfterUnlock = useCallback((dekBytes) => {
    clearIdleTimer();
    setDek((prev) => {
      if (prev) void zeroBytes(prev);
      return dekBytes;
    });
  }, [clearIdleTimer]);

  const handleUnlock = async () => {
    setError('');
    if (!unlockPw) {
      setError('Master password is required');
      return;
    }
    setBusy(true);
    try {
      const saltRes = await getVaultSalt();
      if (!saltRes.data?.success) {
        throw new Error(saltRes.data?.message || 'Could not load vault salt');
      }
      const kdfSalt = saltRes.data?.data?.kdfSalt;
      if (!kdfSalt) {
        throw new Error(saltRes.data?.message || 'Could not load vault salt');
      }
      const saltBytes = base64ToBytes(kdfSalt);
      const { kek, authHash } = await deriveKeys(unlockPw, saltBytes);
      const unlockRes = await unlockVaultApi(authHash);
      if (!unlockRes.data?.success) {
        await zeroBytes(kek);
        throw new Error(unlockRes.data?.message || 'Unlock failed');
      }
      const enc = unlockRes.data?.data?.encryptedDek;
      if (!enc) {
        await zeroBytes(kek);
        throw new Error(unlockRes.data?.message || 'Unlock failed');
      }
      const dekBytes = await unwrapDekFromPayload(enc, kek);
      await zeroBytes(kek);
      setUnlockPw('');
      clearIdleTimer();
      setDek(dekBytes);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Unlock failed';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleSetup = async () => {
    setError('');
    setBusy(true);
    try {
      const bundle = await prepareCorporateVaultSetup(setupPw, setupPw2);
      await setupVaultApi({
        kdfSalt: bundle.kdfSalt,
        authHash: bundle.authHash,
        encryptedDek: bundle.encryptedDek,
      });
      setSetupPw('');
      setSetupPw2('');
      setShowSetupPw(false);
      setShowSetupPw2(false);
      setVaultInitialized(true);
      clearIdleTimer();
      setDek(bundle.dek);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Setup failed';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const value = useMemo(
    () => ({
      vaultInitialized,
      dek,
      vaultReady,
      loading,
      refreshStatus,
      needsZkEncrypt: vaultInitialized && !!dek,
      vaultIdleTimeoutMinutes: idleMinutes,
      personalVaultInitialized,
      personalDek,
      personalVaultUnlocked: !!personalDek,
      refreshPersonalVaultStatus,
      unlockPersonalVaultSession,
      retainCorporateVaultDekAfterUnlock,
      retainPersonalVaultDekAfterSetup,
      clearPersonalVaultSession,
      skipPersonalListPasswordPromptOnce,
      touchCredentialVaultIdleFromActivity,
      touchPersonalVaultIdleFromActivity,
    }),
    [
      vaultInitialized,
      dek,
      vaultReady,
      loading,
      refreshStatus,
      idleMinutes,
      personalVaultInitialized,
      personalDek,
      refreshPersonalVaultStatus,
      unlockPersonalVaultSession,
      retainCorporateVaultDekAfterUnlock,
      retainPersonalVaultDekAfterSetup,
      clearPersonalVaultSession,
      skipPersonalListPasswordPromptOnce,
      touchCredentialVaultIdleFromActivity,
      touchPersonalVaultIdleFromActivity,
    ]
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <CredentialVaultCryptoContext.Provider value={value}>
      <ToastContainer position="top-right" />
      {!vaultInitialized && isAdmin && isCorporateAccount && !isPersonalVaultRoute && !vaultSettingsRouteBypass ? (
        <div className="credential-vault-form-page credential-vault-init-page">
          <Row className="g-4 align-items-stretch credential-vault-init-two-col-row">
            <Col xs={12} lg={7} className="credential-vault-init-main-col">
              <div className="credential-vault-settings-layout credential-vault-settings-layout--split">
                <Card className="user-list-card credential-form-card">
                  <Card.Body style={{ overflow: 'visible', padding: '1.5rem' }}>
                    <h1 className="credential-vault-init-title">Credentials Vault Initial Settings</h1>
                    <p className="credential-form-intro credential-vault-init-lead">
                      To protect your Credentials Vault using highly secure method, we require you to create a Master
                      Key that is only available to you and your team.
                    </p>
                    <div className="credential-vault-init-important" role="note">
                      <i className="fas fa-exclamation-triangle credential-vault-init-important__icon" aria-hidden />
                      <div>
                        <strong className="credential-vault-init-important__label">Important: </strong>
                        <span>
                          This password will not be stored and hence cannot be retrieved if lost. If this password is
                          lost, you will not be able to retrieve any of your stored Credentials from flowdo.
                        </span>
                      </div>
                    </div>
                    <div className="credential-vault-gate-fields" data-credential-vault-corporate-setup>
                      {error ? <div className="text-danger small mb-3">{error}</div> : null}
                      <div className="mb-3">
                        <label className="form-label" htmlFor="credential-vault-corporate-master-setup">
                          Master Key
                        </label>
                        <div className="position-relative password-input-wrapper">
                          <input
                            id="credential-vault-corporate-master-setup"
                            className="form-control"
                            autoFocus
                            type={showSetupPw ? 'text' : 'password'}
                            autoComplete="vaultUnlock"
                            name="credentialVaultCorporateMasterSetup"
                            value={setupPw}
                            onChange={(ev) => setSetupPw(ev.target.value)}
                            onKeyDown={(ev) => {
                              if (ev.key === 'Enter' && !busy) {
                                ev.preventDefault();
                                void handleSetup();
                              }
                            }}
                            disabled={busy}
                          />
                          <button
                            type="button"
                            className="password-toggle-btn"
                            onClick={() => setShowSetupPw((v) => !v)}
                            disabled={busy}
                            aria-label={showSetupPw ? 'Hide password' : 'Show password'}
                          >
                            <i className={`fas ${showSetupPw ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
                          </button>
                        </div>
                      </div>
                      <div className="mb-4">
                        <label className="form-label" htmlFor="credential-vault-corporate-master-setup-confirm">
                          Confirm Master Key
                        </label>
                        <div className="position-relative password-input-wrapper">
                          <input
                            id="credential-vault-corporate-master-setup-confirm"
                            className="form-control"
                            type={showSetupPw2 ? 'text' : 'password'}
                            autoComplete="new-password"
                            name="credentialVaultCorporateMasterSetupConfirm"
                            value={setupPw2}
                            onChange={(ev) => setSetupPw2(ev.target.value)}
                            onKeyDown={(ev) => {
                              if (ev.key === 'Enter' && !busy) {
                                ev.preventDefault();
                                void handleSetup();
                              }
                            }}
                            disabled={busy}
                          />
                          <button
                            type="button"
                            className="password-toggle-btn"
                            onClick={() => setShowSetupPw2((v) => !v)}
                            disabled={busy}
                            aria-label={showSetupPw2 ? 'Hide password' : 'Show password'}
                          >
                            <i className={`fas ${showSetupPw2 ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
                          </button>
                        </div>
                      </div>
                      <div className="task-form-toolbar toolbar toolbar-bottom pt-2">
                        <button
                          type="button"
                          className="new-user-btn credential-vault-gate-primary-btn"
                          disabled={busy}
                          onClick={() => void handleSetup()}
                        >
                          {busy ? (
                            <Spinner animation="border" size="sm" variant="light" className="me-2" />
                          ) : (
                            <i className="fas fa-shield-alt me-2" aria-hidden />
                          )}
                          Initialize Credentials Vault
                        </button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </Col>
            <Col xs={12} lg={5} className="credential-vault-init-aside-col">
              <CredentialVaultInitInfoPanel scope="corporate" />
            </Col>
          </Row>
          <InternalFooter />
        </div>
      ) : null}

      {!vaultInitialized && isAdmin && !isCorporateAccount && !isPersonalVaultRoute ? (
        <CredentialVaultGateShell>
          <Alert variant="light" className="credential-vault-full-page-alert credential-vault-full-page-alert--info">
            The Credentials Vault (master password) is only available for corporate accounts.
            Please contact your administrator.
          </Alert>
        </CredentialVaultGateShell>
      ) : null}

      {!vaultInitialized && !isAdmin && isCorporateAccount && !isPersonalVaultRoute && !vaultSettingsRouteBypass ? (
        <CredentialVaultGateShell>
          <Alert variant="light" className="credential-vault-full-page-alert credential-vault-full-page-alert--info">
          Your Corporate Credentials Vault is not yet setup by the Admin. You can start using this feature once the setup is complete and Corporate Master password is available. Please note that you will need the Master Password (key) to access this section
          </Alert>
        </CredentialVaultGateShell>
      ) : null}

      {!vaultInitialized && !isAdmin && !isCorporateAccount && !isPersonalVaultRoute ? (
        <CredentialVaultGateShell>
          <Alert variant="light" className="credential-vault-full-page-alert credential-vault-full-page-alert--info">
            The Credentials Vault (master password) is only available for corporate accounts.
            Please contact your administrator.
          </Alert>
        </CredentialVaultGateShell>
      ) : null}

      {vaultInitialized &&
      !dek &&
      isCorporateAccount &&
      !personalVaultRouteBypass &&
      !vaultSettingsRouteBypass &&
      !credentialVaultFormEntryBypass &&
      !credentialVaultDetailBypass &&
      !corporateListPasswordSkipBypass ? (
        <CredentialVaultGateShell>
          <Alert variant="light" className="credential-vault-full-page-alert">
            <Alert.Heading className="h5 mb-3">Unlock Credentials Vault</Alert.Heading>
            <div className="credential-vault-gate-fields" data-credential-vault-corporate-unlock>
              <p className="small text-muted mb-3">
                Enter your corporate master password.
                {' '}
                The Credentials Vault locks automatically after {idleMinutes} minute{idleMinutes === 1 ? '' : 's'} of inactivity.
              </p>
              {error ? <div className="text-danger small mb-3">{error}</div> : null}
              <div className="mb-3">
                <label className="form-label" htmlFor="credential-vault-corporate-gate-unlock">
                  Master password
                </label>
                <div className="position-relative password-input-wrapper">
                  <input
                    id="credential-vault-corporate-gate-unlock"
                    className="form-control"
                    autoFocus
                    type={showUnlockPw ? 'text' : 'password'}
                    autoComplete="off"
                    name="credentialVaultCorporateGateUnlockNonce"
                    readOnly={!corporateGateUnlockPrimed}
                    onFocus={() => setCorporateGateUnlockPrimed(true)}
                    value={unlockPw}
                    onChange={(ev) => setUnlockPw(ev.target.value)}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter' && !busy) {
                        ev.preventDefault();
                        void handleUnlock();
                      }
                    }}
                    disabled={busy}
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowUnlockPw((v) => !v)}
                    disabled={busy}
                    aria-label={showUnlockPw ? 'Hide password' : 'Show password'}
                  >
                    <i className={`fas ${showUnlockPw ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden />
                  </button>
                </div>
              </div>
              <div className="task-form-toolbar toolbar toolbar-bottom pt-2">
                <button
                  type="button"
                  className="new-user-btn credential-vault-gate-primary-btn"
                  disabled={busy}
                  onClick={() => void handleUnlock()}
                >
                  {busy ? (
                    <Spinner animation="border" size="sm" variant="light" className="me-2" />
                  ) : (
                    <i className="fas fa-unlock me-2" aria-hidden />
                  )}
                  Unlock
                </button>
              </div>
            </div>
          </Alert>
        </CredentialVaultGateShell>
      ) : null}

      {vaultInitialized && !dek && !isCorporateAccount ? (
        <CredentialVaultGateShell>
          <Alert variant="light" className="credential-vault-full-page-alert credential-vault-full-page-alert--info">
            The Credentials Vault (master password) is only available for corporate accounts.
            Please contact your administrator.
          </Alert>
        </CredentialVaultGateShell>
      ) : null}

      {showVaultOutlet ? children : null}
    </CredentialVaultCryptoContext.Provider>
  );
}
